import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import _get from "lodash/get";
import moment from "moment";
import { Button, Modal, Select, message } from "antd";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  PlusOutlined,
  RobotOutlined
} from "@ant-design/icons";

import paginationFunc from "../../../../helper/hooks/pagination";
import RouteHeader from "../components/header";
import RouteTable from "../components/table/RouteTable";
import {
  getRouteList,
  assignDriver,
  reassignDriver,
  cancelRoute,
  unassignDriver
} from "../store/RouteThunks";
import { selectAllDrivers } from "modules/admin/drivers/store/slice";
import { getDriverList } from "modules/admin/drivers/store/thunks";
import {
  selectAllRoutes,
  selectMetadata,
  updatePaginationRoutes,
  selectFilter,
  updateFilterRoutes,
  selectSort,
  updateSortRoutes
} from "../store/RouteSlice";
import ImportLocations from "modules/client/routes/components/exports";
import { getPackageTypeList } from "modules/client/package-types/store/thunks";
import { doExportCsv } from "helper/export";
import FilterRoute from "modules/client/routes/components/header/FilterRoute";
import { buildFirstLevelFilterRoute } from "modules/shared/PickupDropoff/helper";
import { setSuccessMessage } from "app/global/slice";
import { allowPermission, fullName } from "helper/util";
import { selectGroup, selectRole } from "modules/auth/store/authSlice";
import { UserGroup, canCreatePermission } from "configs/constants";
import { buildFirstLevelFilter } from "modules/admin/clients/helper";
import facturaApi from "modules/admin/facturas/store/api";
import { isEmpty } from "lodash";
import invoicingApi from "modules/admin/invoicing/store/api";
import { shortId } from "helper/util";
import { INVOICE_TYPES } from "configs/constants";

import "../styles/routes.scss";
import ImportHistoricalJobs from "modules/client/routes/components/exports/ImportHistoricalJobs";
import { setGlobalLoading } from "app/global/slice";

const params = { group: "flexio" };

const RouteList = () => {
  const [showFilter, setShowFilter] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showImportHistorical, setShowImportHistorical] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showAssignDriver, setShowAssignDriver] = useState(false);
  const [currentRoute, setCurrentRoute] = useState({});
  const [assignedDriverId, setAssignedDriverId] = useState("");
  const [clientDrivers, setClientDrivers] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showAddToFacturaModal, setShowAddToFacturaModal] = useState(false);
  const [unpaidFacturas, setUnpaidFacturas] = useState([]);
  const [selectedFacturaId, setSelectedFacturaId] = useState(null);
  const [addingToExisting, setAddingToExisting] = useState(false);
  const isFlexioAdmin = useSelector(selectGroup) === UserGroup.FLEXIO;
  const isLoading = useSelector((state) => state.global.isLoading);
  const dispatch = useDispatch();
  const routeList = useSelector(selectAllRoutes);
  const metadata = useSelector(selectMetadata);
  const filter = useSelector(selectFilter);
  const sort = useSelector(selectSort);
  const pagination = paginationFunc(
    { ...metadata, itemsPerPage: _get(metadata, "itemsPerPage", 10) },
    "Active Job",
    updatePaginationRoutes,
    dispatch
  );

  const breadCrumbs = [{ text: "ACTIVE ROUTES" }];
  const canCreate = allowPermission(useSelector(selectRole), canCreatePermission);
  const history = useHistory();

  const buttonParams =
    canCreate && isFlexioAdmin
      ? {
          text: "Create New Job",
          link: "/admin/jobs/new?action=new"
        }
      : null;

  const handleAIImportClick = () => {
    history.push("/admin/ai-import");
  };
  const page = metadata?.currentPage || 1;
  const pageSize = metadata?.itemsPerPage || 10;
  const allDrivers = useSelector(selectAllDrivers);

  useEffect(() => {
    dispatch(getPackageTypeList());
    dispatch(
      getDriverList({
        pageSize: 3000,
        filter: buildFirstLevelFilter({ active: true, is_internal_driver: false })
      })
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!allDrivers?.length) return;
    setClientDrivers(allDrivers);
    // eslint-disable-next-line
  }, [allDrivers]);

  const refreshRoutes = () => {
    const { pickupDateRange = [], closedDateRange = [] } = filter || {};
    let updatedFilter = { ...filter };

    if (pickupDateRange?.length === 2) {
      updatedFilter = {
        ...updatedFilter,
        startPickupDate: pickupDateRange[0],
        endPickupDate: pickupDateRange[1]
      };
    }

    if (closedDateRange?.length === 2) {
      updatedFilter = {
        ...updatedFilter,
        startClosedDate: closedDateRange[0],
        endClosedDate: closedDateRange[1]
      };
    }

    console.log("RouteList - refreshRoutes called with:", {
      page,
      pageSize,
      metadata,
      routeListLength: routeList.length,
      sort,
      filter
    });

    dispatch(
      getRouteList({
        ...params,
        page: page,
        pageSize: pageSize,
        filter: buildFirstLevelFilterRoute(updatedFilter),
        pickupFilter: filter,
        sort: sort ? `${sort.field},${sort.order}` : undefined
      })
    );
  };

  useEffect(() => {
    refreshRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filter, sort]);

  const handleExportRoutes = async () => {
    try {
      const { pickupDateRange = [], closedDateRange = [] } = filter;
      let updatedFilter = { ...filter };

      if (pickupDateRange?.length === 2) {
        updatedFilter = {
          ...updatedFilter,
          startPickupDate: pickupDateRange[0],
          endPickupDate: pickupDateRange[1]
        };
      }

      if (closedDateRange?.length === 2) {
        updatedFilter = {
          ...updatedFilter,
          startClosedDate: closedDateRange[0],
          endClosedDate: closedDateRange[1]
        };
      }

      setExporting(true);
      await doExportCsv({
        path: "routes/export",
        name: `Active-Jobs-${new Date().getTime()}`,
        params: {
          filter: buildFirstLevelFilterRoute(updatedFilter),
          pickupFilter: filter
        }
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportOldInvoices = async () => {
    try {
      const { pickupDateRange = [], status } = filter || {};
      const filterStatuses = isEmpty(status) ? ["canceled", "completed"] : status;

      setExporting(true);
      await doExportCsv({
        path: "routes/export-invoicing",
        name: `invoicing-${new Date().getTime()}`,
        params: {
          filter: buildFirstLevelFilterRoute(
            pickupDateRange?.length === 2
              ? {
                  ...filter,
                  status: filterStatuses,
                  startPickupDate: pickupDateRange[0],
                  endPickupDate: pickupDateRange[1]
                }
              : { ...filter, status: filterStatuses }
          ),
          // Client, driver, service and factura selections live here, not in `filter`
          pickupFilter: { ...filter, status: filterStatuses }
        }
      });
    } finally {
      setExporting(false);
    }
  };

  const handleClickFilter = (values) => {
    dispatch(updateFilterRoutes(values));
  };

  const handleSortChange = (field, order) => {
    dispatch(updateSortRoutes({ field, order }));
  };

  const handleAssignDriverClick = (route) => {
    setCurrentRoute(route);
    setAssignedDriverId(route?.driverId);
    setShowAssignDriver(true);
  };
  const handleUnassignDriver = async (routeId) => {
    try {
      const { error } = await dispatch(unassignDriver(routeId));
      if (!error) {
        dispatch(setSuccessMessage("Driver removed successfully!"));
        refreshRoutes();
      }
    } catch (err) {
      console.log(err);
    }
  };
  const handleSubmitAssignDriver = async () => {
    if (!assignedDriverId) return;
    try {
      const { error } = await dispatch(
        currentRoute?.driverId
          ? reassignDriver({ routeId: currentRoute?.id, driverId: assignedDriverId })
          : assignDriver({ routeId: currentRoute?.id, driverId: assignedDriverId })
      );
      if (!error) {
        dispatch(
          setSuccessMessage(
            `${currentRoute?.driverId ? "Reassigned" : "Assigned"} successfully!`
          )
        );
        setShowAssignDriver(false);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmitCancelRoute = async (routeId) => {
    try {
      const { error } = await dispatch(cancelRoute(routeId));
      if (!error) {
        dispatch(setSuccessMessage("Canceled job successfully!"));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleRouteSelection = (routeIds) => {
    setSelectedRoutes(routeIds);
  };

  const getSelectedRoutesInfo = () => {
    if (selectedRoutes.length === 0) return null;

    const selectedRouteData = routeList.filter((route) =>
      selectedRoutes.includes(route.id)
    );
    const clients = [
      ...new Set(
        selectedRouteData.map((route) => route.client?.clientName).filter(Boolean)
      )
    ];
    const totalAmount = selectedRouteData.reduce(
      (sum, route) => sum + parseFloat(route.clientFinalPrice || 0),
      0
    );

    return {
      count: selectedRoutes.length,
      clients,
      totalAmount: totalAmount.toFixed(2),
      routes: selectedRouteData
    };
  };

  const handleCreateInvoice = async () => {
    const routesInfo = getSelectedRoutesInfo();

    if (!routesInfo) {
      message.error("Please select at least one route");
      return;
    }

    if (routesInfo.clients.length > 1) {
      message.error(
        `Routes must belong to the same client. Selected routes have different clients: ${routesInfo.clients.join(
          ", "
        )}`
      );
      return;
    }

    if (routesInfo.clients.length === 0) {
      message.error("Selected routes must have a client assigned");
      return;
    }

    try {
      setCreatingInvoice(true);

      const response = await facturaApi.createFacturaFromSelectedRoutes(selectedRoutes);

      if (response && response.id) {
        message.success(
          `Invoice ${response.facturaId} created successfully with ${routesInfo.count} route(s) for ${routesInfo.clients[0]}!`
        );
        setSelectedRoutes([]);
        refreshRoutes(); // Refresh the route list to show updated factura status
      } else {
        message.error("Failed to create invoice");
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      message.error(error?.response?.data?.message || "Failed to create invoice");
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleShowAddToExistingInvoice = async () => {
    const routesInfo = getSelectedRoutesInfo();

    if (!routesInfo) {
      message.error("Please select at least one route");
      return;
    }

    if (routesInfo.clients.length > 1) {
      message.error(
        `Routes must belong to the same client. Selected routes have different clients: ${routesInfo.clients.join(
          ", "
        )}`
      );
      return;
    }

    if (routesInfo.clients.length === 0) {
      message.error("Selected routes must have a client assigned");
      return;
    }

    // Get client ID from the first route (all should be same client)
    const firstRoute = routesInfo.routes[0];
    if (!firstRoute?.client?.id) {
      message.error("Unable to determine client for selected routes");
      return;
    }

    setAddingToExisting(true);
    try {
      const response = await facturaApi.getUnpaidFacturas(firstRoute.client.id);
      const facturas = response.data || [];

      if (facturas.length === 0) {
        message.info(
          "No unpaid invoices found for this client. Create a new invoice instead."
        );
        return;
      }

      setUnpaidFacturas(facturas);
      setShowAddToFacturaModal(true);
    } catch (error) {
      console.error("Load facturas error:", error);
      const errorMessage =
        error?.response?.data?.message || error?.message || "Unknown error";
      message.error("Failed to load unpaid invoices: " + errorMessage);
    } finally {
      setAddingToExisting(false);
    }
  };

  const handleAddToExistingInvoice = async () => {
    if (!selectedFacturaId) {
      message.error("Please select an invoice");
      return;
    }

    setAddingToExisting(true);
    try {
      await facturaApi.addRoutesToFactura(selectedFacturaId, selectedRoutes);
      message.success(`${selectedRoutes.length} route(s) added to invoice successfully!`);

      setSelectedRoutes([]);
      setShowAddToFacturaModal(false);
      setSelectedFacturaId(null);
      refreshRoutes(); // Refresh the route list to show updated factura status
    } catch (error) {
      console.error("Add to factura error:", error);
      const errorMessage =
        error?.response?.data?.message || error?.message || "Unknown error";
      message.error("Failed to add routes to invoice: " + errorMessage);
    } finally {
      setAddingToExisting(false);
    }
  };

  const handleModalCancel = () => {
    setShowAddToFacturaModal(false);
    setSelectedFacturaId(null);
  };

  const handleExportInvoicePdf = async (routeId, invoiceType) => {
    try {
      // Find the route data to extract client name and service date
      const routeData = routeList.find((route) => route.id === routeId);
      const clientName = routeData?.client?.clientName || "Unknown-Client";
      const serviceDate = routeData?.startDate || "Unknown-Date";
      const invoiceId = shortId(routeId);
      const invoiceSuffix = invoiceType === INVOICE_TYPES.DRIVER_INVOICE ? "_driver" : "";

      // Create custom filename: "Client Name_Invoice ID_Service Date[_driver].pdf"
      const customFilename =
        `${clientName}_${invoiceId}_${serviceDate}${invoiceSuffix}.pdf`.replace(
          /[^\w\s.-]/gi,
          ""
        );

      const response = await invoicingApi.exportInvoicePdf(routeId, invoiceType);
      const blob = new Blob([response], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = customFilename;

      // Add link to DOM, click it, then remove it (for direct download with custom filename)
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.log(err);
      message.error("Failed to export invoice PDF");
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px"
        }}
      >
        <div style={{ flex: 1 }}>
          <RouteHeader
            breadCrumbs={breadCrumbs}
            buttonParams={buttonParams}
            showFilter={showFilter}
            setShowFilter={setShowFilter}
            showExport={true}
            showImport={canCreate && isFlexioAdmin}
            setShowImport={setShowImport}
            setShowImportHistorical={setShowImportHistorical}
            exporting={exporting}
            onExport={handleExportRoutes}
            onExportOldInvoices={handleExportOldInvoices}
            isAdmin
          />
        </div>
        {canCreate && isFlexioAdmin && (
          <Button
            type="primary"
            icon={<RobotOutlined />}
            onClick={handleAIImportClick}
            size="small"
            style={{
              backgroundColor: "#00e2dc",
              borderColor: "#00e2dc",
              color: "#004dbc",
              marginBottom: "20px"
            }}
          >
            AI Import
          </Button>
        )}
      </div>
      {showFilter && (
        <FilterRoute
          isAdmin={true}
          defaultValues={filter}
          setShowFilter={setShowFilter}
          handleClickFilter={handleClickFilter}
          onReset={() =>
            dispatch(
              updateFilterRoutes({
                status: ["pending", "accepted", "in-progress"],
                pickupDateRange: [
                  moment().subtract(1, "month").startOf("month"),
                  moment().endOf("month")
                ]
              })
            )
          }
        />
      )}

      {/* Selected Routes Bar */}
      {selectedRoutes.length > 0 && (
        <div className="admin-content" style={{ marginBottom: 16 }}>
          <div
            style={{
              background: "#f0f8ff",
              border: "1px solid #d1ecf1",
              borderRadius: "4px",
              padding: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <div>
              <span style={{ fontWeight: "bold", color: "#0c5460" }}>
                {selectedRoutes.length} route(s) selected
              </span>
              {(() => {
                const info = getSelectedRoutesInfo();
                return (
                  info && (
                    <span style={{ marginLeft: 16, color: "#495057" }}>
                      Client: {info.clients[0] || "N/A"} | Total: ${info.totalAmount}
                    </span>
                  )
                );
              })()}
            </div>
            <div>
              <Button onClick={() => setSelectedRoutes([])} style={{ marginRight: 8 }}>
                Clear Selection
              </Button>
              <Button
                icon={<PlusOutlined />}
                loading={addingToExisting}
                onClick={handleShowAddToExistingInvoice}
                disabled={selectedRoutes.length === 0}
                style={{ marginRight: 8 }}
              >
                Add to Existing Invoice
              </Button>
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                loading={creatingInvoice}
                onClick={handleCreateInvoice}
                disabled={selectedRoutes.length === 0}
              >
                Create Invoice
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-content">
        <RouteTable
          dataSource={routeList}
          pagination={pagination}
          onCancelRoute={handleSubmitCancelRoute}
          onAssignDriverClick={handleAssignDriverClick}
          onReassignDriverClick={handleAssignDriverClick}
          onUnassignDriverClick={handleUnassignDriver}
          hiddenColumns={["Min. Vehicle"]}
          selectedRoutes={selectedRoutes}
          onRouteSelection={handleRouteSelection}
          enableMultiSelect={isFlexioAdmin}
          onRefresh={refreshRoutes}
          onSortChange={handleSortChange}
          currentSort={sort}
          onExportInvoicePdf={handleExportInvoicePdf}
        />
      </div>
      {showImport && (
        <Modal
          closable={false}
          visible={true}
          title={"Import Routes Data"}
          className="no-padding-modal"
          onCancel={() => {
            setShowImport(false);
            dispatch(setGlobalLoading(false));
          }}
          footer={false}
          width={1000}
        >
          <ImportLocations handleCloseModal={setShowImport} isAdmin />
        </Modal>
      )}
      {showImportHistorical && (
        <Modal
          closable={false}
          visible={true}
          title={"Import Historical Jobs Data"}
          className="no-padding-modal"
          onCancel={() => setShowImportHistorical(false)}
          footer={false}
          width={1000}
        >
          <ImportHistoricalJobs
            handleCloseModal={setShowImportHistorical}
            isAdmin
            refreshRoutes={refreshRoutes}
          />
        </Modal>
      )}
      {showAssignDriver && (
        <Modal
          closable={false}
          visible={true}
          title={`${currentRoute?.driverId ? "Reassign" : "Assign"} Driver`}
          width={600}
          footer={
            <div className="buttons-modal">
              <Button size="large" onClick={() => setShowAssignDriver(false)}>
                Cancel
              </Button>
              <Button
                disabled={
                  !assignedDriverId || assignedDriverId === currentRoute?.driverId
                }
                loading={isLoading}
                size="large"
                type="primary"
                onClick={handleSubmitAssignDriver}
              >
                Assign
                <CheckCircleOutlined />
              </Button>
            </div>
          }
        >
          <Select
            size="large"
            placeholder="Select driver"
            onChange={(driverId) => setAssignedDriverId(driverId)}
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="children"
            value={assignedDriverId}
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input?.toLowerCase())
            }
          >
            {clientDrivers.map((driver) => (
              <Select.Option key={driver.id} value={driver.id}>
                {`${fullName(driver)} (${driver.email})`}
              </Select.Option>
            ))}
          </Select>
        </Modal>
      )}

      {/* Add to Existing Invoice Modal */}
      {showAddToFacturaModal && (
        <Modal
          title="Add Routes to Existing Invoice"
          open={showAddToFacturaModal}
          onOk={handleAddToExistingInvoice}
          onCancel={handleModalCancel}
          confirmLoading={addingToExisting}
          okText="Add to Invoice"
          okButtonProps={{ disabled: !selectedFacturaId }}
        >
          <div style={{ marginBottom: 16 }}>
            <p>
              Select an unpaid invoice to add {selectedRoutes.length} selected route(s)
              to:
            </p>
          </div>
          <Select
            placeholder="Select an invoice"
            style={{ width: "100%" }}
            value={selectedFacturaId}
            onChange={setSelectedFacturaId}
            showSearch
            optionFilterProp="children"
          >
            {unpaidFacturas.map((factura) => (
              <Select.Option key={factura.id} value={factura.id}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                    {factura.facturaId}
                  </span>
                  <span style={{ color: "#666", fontSize: "12px" }}>
                    ${(Number(factura.total) || 0).toFixed(2)}
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
          {unpaidFacturas.length === 0 && (
            <div style={{ textAlign: "center", color: "#999", marginTop: 16 }}>
              No unpaid invoices available
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};
export default RouteList;
